<section class="page">
  <a [routerLink]="backRoute()" class="back-link">{{ backLabel() }}</a>

  @if (loading()) {
    <p class="state">{{ backofficePreview() ? t('bo.bracketPreview.loading') : t('public.bracket.loading') }}</p>
  } @else if (error()) {
    <p class="state state--error">{{ error() }}</p>
  } @else if (board(); as data) {
    <header class="hero card">
      <div class="hero-copy">
        <p class="eyebrow">{{ pageEyebrow() }}</p>
        <h1>{{ data.tournament.name }}</h1>
        <p class="sub">
          {{ data.bracket.name }} - {{ bracketKindLabel(data.bracket.bracket_kind) }}
        </p>
        <p class="legend">{{ pageLegend(data.bracket.status) }}</p>
      </div>

      <div class="hero-meta">
        <span class="pill">{{ data.bracket.rounds.length }} rounds</span>
        <span class="pill pill--muted">schema v{{ data.schemaVersion }}</span>
        <span class="pill pill--muted">{{ data.tournament.slug }}</span>
      </div>
    </header>

    <section class="board">
      <div class="section-head">
        <h2>Bracket principal</h2>
        <p>Lecture horizontale continue de gauche a droite.</p>
        @if (stages().length > 0 && !isDoubleEliminationBoard() && !isSingleEliminationBoard()) {
          <div class="stage-pills">
            @for (stage of stages(); track stage.key) {
              <span class="pill">{{ stageLabel(stage.key) }}</span>
            }
          </div>
        }
      </div>

      @if (isDoubleEliminationBoard()) {
        <section class="de-canvas" aria-label="Double elimination bracket">
          <div
            class="de-flow"
            [class.de-flow--de16]="isDe16WithReset()"
            [style.--de-cols]="deVisualColumnCount()"
          >
            <p class="de-row-label">Winner bracket</p>
            <div class="de-row de-row--winner" role="list" aria-label="Winner bracket rounds">
              @for (round of deLayout().winner; track round.id; let roundIndex = $index) {
                <div
                  class="de-round-shell"
                  role="listitem"
                  [style.grid-column]="deGridColumn(round, 'winner')"
                  [style.margin-top.rem]="deShellOffsetRem(round, 'winner')"
                >
                  <app-tournament-bracket-round-column
                    [round]="round"
                    [roundIndex]="roundIndex"
                    [totalRounds]="deLayout().winner.length"
                    [previousMatchCount]="roundIndex > 0 ? deLayout().winner[roundIndex - 1].matches.length : null"
                    [explicitMatchOffsetRem]="deRoundMatchOffsetRem(round, 'winner')"
                    [explicitMatchGapRem]="deRoundMatchGapRem(round, 'winner')"
                    [outgoingConnectorSpanRem]="useDeOutgoingFeed(round, 'winner') ? deForwardFeedSpanRem(round, 'winner') : null"
                    [forceOutgoingConnector]="useDeOutgoingFeed(round, 'winner')"
                    [reportScoreHrefByMatchId]="reportScoreHrefByMatchId()"
                    [reportScoreLabelByMatchId]="reportScoreLabelByMatchId()"
                    [reportScoreQueryParams]="reportScoreQueryParams()"
                    [reportScoreLabel]="t('public.bracket.reportScore')"
                    [compact]="true"
                  ></app-tournament-bracket-round-column>
                </div>
              }

              @for (round of deLayout().final; track round.id; let roundIndex = $index) {
                <div
                  class="de-round-shell de-round-shell--final-lane"
                  role="listitem"
                  [style.grid-column]="deGridColumn(round, 'final')"
                  [class.de-round-shell--final-emphasis]="isDeGrandFinalRound(round)"
                  [class.de-round-shell--grand-final]="isDeGrandFinalRound(round)"
                  [class.de-round-shell--reset-final]="isDeResetFinalRound(round)"
                  [class.de-round-shell--reset-skipped]="isDeResetRoundNotNeeded(round)"
                >
                  <app-tournament-bracket-round-column
                    [round]="round"
                    [roundIndex]="roundIndex + 1"
                    [totalRounds]="deLayout().final.length"
                    [previousMatchCount]="roundIndex > 0 ? deLayout().final[roundIndex - 1].matches.length : null"
                    [hideIncomingConnector]="hideDeIncomingConnector(round, 'final')"
                    [inactiveFinalCard]="isDeResetRoundNotNeeded(round)"
                    [inactiveFinalCardLabel]="deFinalRoundStatusLabel(round)"
                    [reportScoreHrefByMatchId]="reportScoreHrefByMatchId()"
                    [reportScoreLabelByMatchId]="reportScoreLabelByMatchId()"
                    [reportScoreQueryParams]="reportScoreQueryParams()"
                    [reportScoreLabel]="t('public.bracket.reportScore')"
                    [compact]="true"
                  ></app-tournament-bracket-round-column>
                </div>
              }
            </div>

            <div class="de-lane-divider" aria-hidden="true"></div>

            <p class="de-row-label">Loser bracket</p>
            <div class="de-row de-row--loser" role="list" aria-label="Loser bracket rounds">
              @for (round of deLayout().loser; track round.id; let roundIndex = $index) {
                <div
                  class="de-round-shell de-round-shell--loser-lane"
                  role="listitem"
                  [style.grid-column]="deGridColumn(round, 'loser')"
                  [style.margin-top.rem]="deShellOffsetRem(round, 'loser')"
                >
                  <app-tournament-bracket-round-column
                    [round]="round"
                    [roundIndex]="roundIndex"
                    [totalRounds]="deLayout().loser.length"
                    [previousMatchCount]="roundIndex > 0 ? deLayout().loser[roundIndex - 1].matches.length : null"
                    [explicitMatchOffsetRem]="deRoundMatchOffsetRem(round, 'loser')"
                    [explicitMatchGapRem]="deRoundMatchGapRem(round, 'loser')"
                    [reportScoreHrefByMatchId]="reportScoreHrefByMatchId()"
                    [reportScoreLabelByMatchId]="reportScoreLabelByMatchId()"
                    [reportScoreQueryParams]="reportScoreQueryParams()"
                    [reportScoreLabel]="t('public.bracket.reportScore')"
                    [compact]="true"
                  ></app-tournament-bracket-round-column>
                </div>
              }
            </div>
          </div>
        </section>
      } @else if (isSingleEliminationBoard()) {
        <div class="rounds" role="list" aria-label="Tournament bracket rounds">
          @for (round of rounds(); track round.id; let roundIndex = $index) {
            <div class="round-shell" role="listitem" [style.margin-top.rem]="singleRoundShellOffsetRem(round)">
              <app-tournament-bracket-round-column
                [round]="round"
                [roundIndex]="roundIndex"
                [totalRounds]="rounds().length"
                [previousMatchCount]="roundIndex > 0 ? rounds()[roundIndex - 1].matches.length : null"
                [explicitMatchOffsetRem]="singleRoundMatchOffsetRem(round)"
                [explicitMatchGapRem]="singleRoundMatchGapRem(round)"
                [joinIncomingConnector]="round.stage === 'final'"
                [reportScoreHrefByMatchId]="reportScoreHrefByMatchId()"
                [reportScoreLabelByMatchId]="reportScoreLabelByMatchId()"
                [reportScoreQueryParams]="reportScoreQueryParams()"
                [reportScoreLabel]="t('public.bracket.reportScore')"
              ></app-tournament-bracket-round-column>
            </div>
          }
        </div>
      } @else if (stageSections().length > 0) {
        <div class="stage-layout">
          @for (section of stageSections(); track section.key) {
            <section class="stage-board" [attr.data-stage]="section.key">
              <header class="stage-head">{{ stageLabel(section.key) }}</header>

              <div class="rounds" role="list" aria-label="Tournament bracket rounds">
                @for (round of section.rounds; track round.id; let roundIndex = $index) {
                  <div class="round-shell" role="listitem">
                  <app-tournament-bracket-round-column
                    [round]="round"
                    [roundIndex]="roundIndex"
                    [totalRounds]="section.rounds.length"
                    [previousMatchCount]="roundIndex > 0 ? section.rounds[roundIndex - 1].matches.length : null"
                    [reportScoreHrefByMatchId]="reportScoreHrefByMatchId()"
                    [reportScoreLabelByMatchId]="reportScoreLabelByMatchId()"
                    [reportScoreQueryParams]="reportScoreQueryParams()"
                    [reportScoreLabel]="t('public.bracket.reportScore')"
                  ></app-tournament-bracket-round-column>
                  </div>
                }
              </div>
            </section>
          }
        </div>
      } @else {
        <div class="rounds" role="list" aria-label="Tournament bracket rounds">
          @for (round of rounds(); track round.id; let roundIndex = $index) {
            <div class="round-shell" role="listitem">
              <app-tournament-bracket-round-column
                [round]="round"
                [roundIndex]="roundIndex"
                [totalRounds]="rounds().length"
                [reportScoreHrefByMatchId]="reportScoreHrefByMatchId()"
                [reportScoreLabelByMatchId]="reportScoreLabelByMatchId()"
                [reportScoreQueryParams]="reportScoreQueryParams()"
                [reportScoreLabel]="t('public.bracket.reportScore')"
              ></app-tournament-bracket-round-column>
            </div>
          }
        </div>
      }
    </section>
  }
</section>
